import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Paper, Tab, Tabs, Box, Typography, Select, MenuItem } from '@material-ui/core';
import BreadCrumbs from '../../../components/BreadCrumbs/BreadCrumbs';
import paths from '../../../_helpers/paths';
import { ROLES } from '../../../_helpers/Constants';
import PERMISSIONS from '../../../_helpers/permissions';
import { loginModule } from '../../LoginPage/LoginDucks';
import {
  reportsPageModule,
  loadPendingFincenterActs,
  loadSignedFincenterActs,
  exportFincenterAct,
} from '../ReportsPageDucks';
import { reportsFincenterActsColumn } from './ReportsFincenterActsColumn';
import StaticTablePagination from '../../../_ui/Table/StaticTablePaginated';
import useTableFilter from '../../../components/TableFilter/useTableFilter';
import ReportsFincenterActsPage from './ReportsFincenterActsPage';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR - 5 + i);
const DEFAULT_TAB_FILTER = { pageNumber: 1, pageSize: 20, year: CURRENT_YEAR };

const FincenterPendingTab = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector(state => state[reportsPageModule].pendingFincenterActs);
  const loading = useSelector(state => state[reportsPageModule].loadingPendingFincenterActs);

  const { filter, nextPage, changeFilter } = useTableFilter(
    filter => dispatch(loadPendingFincenterActs(filter)),
    DEFAULT_TAB_FILTER
  );

  return (
    <div>
      <Box className="flex items-center mt2 mb1" style={{ gap: 8 }}>
        <Typography style={{ fontSize: '14px' }}>{t('reportsPage_fincenterActPeriod')}</Typography>
        <Select
          value={filter.year || CURRENT_YEAR}
          onChange={e => changeFilter({ year: e.target.value, pageNumber: 1 })}
          variant="outlined"
          style={{ width: 150, height: 40 }}
        >
          {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </Select>
      </Box>
      <StaticTablePagination
        sortable={false}
        loading={loading}
        columns={reportsFincenterActsColumn({
          t,
          language: i18n.language,
          onDownloadAct: docId => dispatch(exportFincenterAct(docId)),
          history,
          tabType: 'pending',
        })}
        totalElements={data.totalElements}
        data={data.content}
        onClickLoadMore={() => nextPage()}
      />
    </div>
  );
};

const FincenterSignedTab = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector(state => state[reportsPageModule].signedFincenterActs);
  const loading = useSelector(state => state[reportsPageModule].loadingSignedFincenterActs);

  const { filter, nextPage, changeFilter } = useTableFilter(
    filter => dispatch(loadSignedFincenterActs(filter)),
    DEFAULT_TAB_FILTER
  );

  return (
    <div>
      <Box className="flex items-center mt2 mb1" style={{ gap: 8 }}>
        <Typography style={{ fontSize: '14px' }}>{t('reportsPage_fincenterActPeriod')}</Typography>
        <Select
          value={filter.year || CURRENT_YEAR}
          onChange={e => changeFilter({ year: e.target.value, pageNumber: 1 })}
          variant="outlined"
          style={{ width: 150, height: 40 }}
        >
          {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </Select>
      </Box>
      <StaticTablePagination
        sortable={false}
        loading={loading}
        columns={reportsFincenterActsColumn({
          t,
          language: i18n.language,
          onDownloadAct: docId => dispatch(exportFincenterAct(docId)),
          history,
          tabType: 'signed',
        })}
        totalElements={data.totalElements}
        data={data.content}
        onClickLoadMore={() => nextPage()}
      />
    </div>
  );
};

const ReportsFincenterActsTabs = ({ location: { pathname, search } }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.login.user);

  const isHead = useMemo(
    () => user?.roles?.includes(ROLES.ES_SERVICE_PROVIDER_HEADS),
    [user]
  );
  const isRegionEmployee = useMemo(
    () => user?.roles?.includes(ROLES.ES_REGION_MIO_EMPLOYEES),
    [user]
  );
  const isFincenter = useMemo(
    () => user?.roles?.includes(ROLES.ES_FINCENTER),
    [user]
  );
  const isAdmin = useSelector(state => state[loginModule].permissions?.includes(PERMISSIONS.admin));

  const generatePath = paths.reportsFincenterTabGenerate;
  const pendingPath = paths.reportsFincenterTabPending;
  const signedPath = paths.reportsFincenterTabSigned;
  const basePath = paths.reportsFincenterActsPage;

  const breadCrumbItems = [
    { name: t('breadcrumbs_home'), href: '/' },
    { name: t('breadcrumbs_reportsFincenterActsPage'), href: basePath },
  ];

  if (pathname === basePath) {
    if (isAdmin) return <Redirect to={generatePath} />;
    if (isFincenter) return <Redirect to={signedPath} />;
    return <Redirect to={isRegionEmployee ? pendingPath : generatePath} />;
  }

  return (
    <>
      <BreadCrumbs items={breadCrumbItems} />
      <Typography variant="h5" style={{ fontWeight: 600, margin: '16px 0 12px' }}>
        {t('reportsPage_fincenterActPage')}
      </Typography>
      <Paper elevation={2}>
        <Tabs
          value={pathname}
          indicatorColor="primary"
          textColor="primary"
          style={{ borderBottom: '1px solid #e0e0e0' }}
        >
          {(isAdmin || isHead) && (
            <Tab
              label={t('reportsPage_fincenterTabGenerate')}
              value={generatePath}
              component={Link}
              to={generatePath + search}
              style={{ textTransform: 'none', fontWeight: 500, minWidth: 140 }}
            />
          )}
          {(isAdmin || isRegionEmployee) && (
            <Tab
              label={t('reportsPage_fincenterTabPending')}
              value={pendingPath}
              component={Link}
              to={pendingPath + search}
              style={{ textTransform: 'none', fontWeight: 500, minWidth: 140 }}
            />
          )}
          {(isAdmin || isRegionEmployee || isFincenter) && (
            <Tab
              label={t('reportsPage_fincenterTabSigned')}
              value={signedPath}
              component={Link}
              to={signedPath + search}
              style={{ textTransform: 'none', fontWeight: 500, minWidth: 140 }}
            />
          )}
        </Tabs>
        <div className="px3 pb3 pt1">
          {pathname === generatePath && <ReportsFincenterActsPage />}
          {pathname === pendingPath && <FincenterPendingTab />}
          {pathname === signedPath && <FincenterSignedTab />}
        </div>
      </Paper>
    </>
  );
};

export default ReportsFincenterActsTabs;
